import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/InfoRounded';
import { isNumber, capitalize, get, set, omit, sortBy } from 'lodash-es';
import { Dialog } from '../../../components/Dialog';
import { NumericTextField } from '../../../components/NumericTextField';
import LocalDate from '../../../components/LocalDate';
import * as api from '../../../lib/api.js';
import { useGrowers } from '../../../hooks/use-growers.js';
import { calculateStats } from '../../../lib/stats.js';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import { useContext } from '../context.jsx';

const constraints = {
  lime: { multiple: 5, min: 0, max: 8000 },
  phosphorus: { multiple: 5, min: 0, max: 400 },
  potassium: { multiple: 5, min: 0, max: 400 },
};

export function InstanceAdjustments(props) {
  const { selectedId: growerId } = useGrowers();
  const { instance, handleClose } = props;
  const [isConfirmingRemove, confirmRemove] = useState(false);
  const [exclude, setExclude] = useState([]);
  const { getAllPrescriptionsForField } = useContext();
  const [adjustments, setAdjustments] = useState({
    lime: { min: '', max: '' },
    phosphorus: { min: '', max: '' },
    potassium: { min: '', max: '' },
  });
  const [errors, setErrors] = useState({});

  const getInputConstraints = useCallback(
    (nutrient, limit) => {
      const otherEnd = limit === 'min' ? 'max' : 'min';
      const otherEndValue = get(adjustments, `${nutrient}.${otherEnd}`);

      return isNumber(otherEndValue)
        ? { ...constraints[nutrient], [otherEnd]: otherEndValue }
        : constraints[nutrient];
    },
    [adjustments],
  );

  const hasAdjusted = useMemo(
    () =>
      exclude.length ||
      Object.values(adjustments).some(
        ({ min, max }) => isNumber(min) || isNumber(max),
      ),
    [adjustments, exclude.length],
  );
  const hasErrors = useMemo(() => Object.keys(errors).length, [errors]);

  return (
    <>
      {buildDeleteFieldConfirmationDialog()}
      <Dialog
        cancelLabel="Close"
        width="lg"
        title="Adjustment Module"
        onCancel={handleClose}
        onSave={handleSave}
        canSave={hasAdjusted && !hasErrors}
      >
        <Box
          sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Typography>{instance.name}</Typography>
          <Box sx={{ display: 'flex' }}>
            {buildAdjustments('lime')}
            {buildAdjustments('phosphorus')}
            {buildAdjustments('potassium')}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>{buildTable()}</Box>
        </Box>
      </Dialog>
    </>
  );

  function handleSave() {
    return api
      .adjustLpkInstance(growerId, instance.id, { ...adjustments, exclude })
      .then(handleClose);
  }

  function handleInputChange(nutrient, key, value) {
    setErrors((current) => omit(current, [`${nutrient}.${key}`]));
    setAdjustments((current) =>
      set({ ...current }, `${nutrient}.${key}`, value),
    );
  }

  function handleInputError(nutrient, key, error) {
    setErrors((current) => ({ ...current, [`${nutrient}.${key}`]: error }));
  }

  function buildAdjustments(nutrient) {
    return (
      <Box
        sx={{
          flex: 1,
          borderRight:
            nutrient === 'potassium' ? null : '1px solid rgba(0, 0, 0, 0.12)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6">{`${capitalize(nutrient)} Adjust`}</Typography>
        <Box sx={{ minHeight: 64 }}>
          <NumericTextField
            fullWidth
            value={adjustments[nutrient]?.min}
            ignoreNonNumericInput
            size="small"
            label={`${capitalize(nutrient)} Min`}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleInputChange(nutrient, 'min', e.target.value)}
            onError={handleInputError.bind(null, nutrient, 'min')}
            {...getInputConstraints(nutrient, 'min')}
          />
        </Box>
        <FormControlLabel
          sx={{ mt: -3.5, ml: 1 }}
          control={
            <Checkbox
              onChange={(e) =>
                handleInputChange(nutrient, 'excludeZero', e.target.checked)
              }
            />
          }
          data-testid={`${nutrient}-exclude-zero`}
          label="Exclude Zero Rates"
        />
        <Box sx={{ minHeight: 64 }}>
          <NumericTextField
            fullWidth
            value={adjustments[nutrient]?.max}
            ignoreNonNumericInput
            size="small"
            label={`${capitalize(nutrient)} Max`}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => handleInputChange(nutrient, 'max', e.target.value)}
            onError={handleInputError.bind(null, nutrient, 'max')}
            {...getInputConstraints(nutrient, 'max')}
          />
        </Box>
      </Box>
    );
  }

  function buildTable() {
    return (
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Field Name</TableCell>
            <TableCell>Num of Cropping Years</TableCell>
            <TableCell>Date Created</TableCell>
            <TableCell>Years to Critical</TableCell>
            <TableCell>L Min</TableCell>
            <TableCell>L Max</TableCell>
            <TableCell>L Avg Lbs/Ac</TableCell>
            <TableCell>P Min</TableCell>
            <TableCell>P Max</TableCell>
            <TableCell>P Avg Lbs/Ac</TableCell>
            <TableCell>K Min</TableCell>
            <TableCell>K Max</TableCell>
            <TableCell>K Avg Lbs/Ac</TableCell>
            <TableCell>Remove Field</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{sortBy(instance.fields, 'name').map(buildField)}</TableBody>
      </Table>
    );
  }

  function buildField(field, i) {
    if (exclude.includes(field.aaid)) return null;
    const prescriptions = getAllPrescriptionsForField(instance.id, field.aaid);

    const instanceInput = (attribute) =>
      ['lime', 'phosphorus', 'potassium']
        .map((nutrient) => prescriptions[nutrient]?.[attribute])
        .filter(Boolean)[0];

    return (
      <TableRow key={i}>
        <TableCell>{field.name}</TableCell>
        <TableCell>{instanceInput('numberOfCroppingYears')}</TableCell>
        <TableCell>
          <LocalDate value={instance.createdAt} />
        </TableCell>
        <TableCell>{instanceInput('yearsToCritical')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'lime', 'min')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'lime', 'max')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'lime', 'avg')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'phosphorus', 'min')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'phosphorus', 'max')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'phosphorus', 'avg')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'potassium', 'min')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'potassium', 'max')}</TableCell>
        <TableCell>{buildStat(prescriptions, 'potassium', 'avg')}</TableCell>
        <TableCell sx={{ pl: 0, pr: 0.5 }}>
          <IconButton
            title="Remove Field"
            size="small"
            data-testid={`remove-field-${i + 1}`}
            onClick={() => confirmRemove(field)}
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }

  function buildStat(field, nutrient, stat) {
    const rx = field[nutrient];

    if (!rx) return null;

    if (/fail/i.test(rx.status)) {
      return stat === 'avg' ? (
        <Tooltip title={rx.statusDetails || rx.status}>
          <InfoIcon color="error" style={{ width: '20px' }} />
        </Tooltip>
      ) : null;
    }

    const stats = calculateAdjustedStats(rx, adjustments[nutrient]);
    return Math.round(stats[stat]);
  }

  function removeField() {
    setExclude((current) => [...current, isConfirmingRemove.aaid]);
    confirmRemove(false);
  }

  function buildDeleteFieldConfirmationDialog() {
    if (!isConfirmingRemove) return null;

    return (
      <ConfirmationDialog
        title="Delete Field from Rx"
        message={`Are you sure you want to remove ${isConfirmingRemove.name}`}
        handleClose={() => confirmRemove(false)}
        handleConfirm={removeField}
      />
    );
  }
}

function calculateAdjustedStats(rx, adjustments) {
  const distribution = rx.stats?.distribution || [];

  const adjustedDistribution = distribution.map(({ acres, value }) => ({
    acres,
    rate: enforceMax(adjustments, enforceMin(adjustments, value)),
  }));

  return calculateStats(adjustedDistribution);
}

function enforceMin(adjustments, value) {
  if (adjustments?.excludeZero && value === 0) return value;
  return isNumber(adjustments?.min) ? Math.max(value, adjustments.min) : value;
}

function enforceMax(adjustments, value) {
  return isNumber(adjustments?.max) ? Math.min(value, adjustments.max) : value;
}
